import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  updateProfile
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from "firebase/firestore";
import { auth, db } from "../lib/firebase";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

/**
 * Roles are stored in Firestore at users/{uid}.role
 * Possible values: 'customer' | 'driver' | 'admin'
 *
 * For demo purposes, login pages can pass `expectedRole` so that if a user
 * has no role doc yet, we'll create one with that role (auto-assignment).
 * In production, remove the auto-assignment and only allow role updates
 * via admin console / Cloud Functions.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load profile (role + name) from Firestore — gracefully handles permission errors
  const loadProfile = useCallback(async (fbUser, expectedRole = "customer") => {
    if (!fbUser) return null;
    const baseProfile = {
      id: fbUser.uid,
      email: fbUser.email,
      name: fbUser.displayName || (fbUser.email ? fbUser.email.split("@")[0] : "User"),
      role: expectedRole
    };
    try {
      const ref = doc(db, "users", fbUser.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        return {
          ...baseProfile,
          name: fbUser.displayName || data.name || baseProfile.name,
          role: data.role || expectedRole,
          ...data
        };
      }
      // First-time login: create profile with expectedRole (demo auto-assign)
      try {
        await setDoc(ref, {
          email: fbUser.email,
          role: expectedRole,
          name: fbUser.displayName || fbUser.email,
          createdAt: serverTimestamp()
        });
      } catch (writeErr) {
        // eslint-disable-next-line no-console
        console.warn("Could not write users/{uid} doc (check Firestore rules):", writeErr?.message);
      }
      return baseProfile;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Could not read users/{uid} doc — using Auth-only profile:", err?.message);
      return baseProfile;
    }
  }, []);

  // Hydrate from Firebase auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      try {
        if (fbUser) {
          const profile = await loadProfile(fbUser);
          setUser(profile);
        } else {
          setUser(null);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Auth state error:", err);
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, [loadProfile]);

  const login = useCallback(async (email, password, expectedRole = "customer") => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const profile = await loadProfile(cred.user, expectedRole);
    setUser(profile);
    return profile;
  }, [loadProfile]);

  const register = useCallback(async ({ email, password, name, phone, company, role = "customer" }) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (name) {
      try { await updateProfile(cred.user, { displayName: name }); } catch {}
    }
    try {
      await setDoc(doc(db, "users", cred.user.uid), {
        email,
        name: name || email,
        phone: phone || "",
        company: company || "",
        role,
        createdAt: serverTimestamp()
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Could not save user profile (check Firestore rules):", err?.message);
    }
    const profile = {
      id: cred.user.uid,
      email,
      name: name || email,
      phone, company, role
    };
    setUser(profile);
    return profile;
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
    setUser(null);
  }, []);

  const resetPassword = useCallback(async (email) => {
    await sendPasswordResetEmail(auth, email);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, resetPassword, loading }}>
      {children}
    </AuthContext.Provider>
  );
}
